import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { VALUES, NEIGHBOUR_IMAGE, GATHERING_IMAGE } from "@/lib/constants";
import { Reveal } from "@/components/Reveal";
import { rememberIntent } from "@/lib/intent";
import {
  ArrowRight, HandHeart, Award, CalendarCheck, HeartHandshake, MapPinned, ShieldCheck,
  Trophy, ScrollText, Medal, Compass,
} from "lucide-react";

// Static marketing sections extracted from pages/Landing.jsx (Phase 15
// component split); copy and behaviour unchanged. Content arrays live at
// module scope so they aren't re-created on every render.

// Phase 17-B item 3 (2026-07): ONE button-styled CTA per pillar — competing
// links folded into the body copy. `intent` keeps the B1 intent-first funnel
// working for Get help.
const THREE_WAYS = [
  {
    icon: HandHeart, accent: "text-terracotta", bg: "bg-terracotta/10",
    title: "Give help",
    body: "Verified roles, quick-match swiping and grassroots events round the corner. Your hours are logged, approved and recognised.",
    cta: { to: "/opportunities", label: "Find opportunities", testId: "way-give-cta" },
  },
  {
    icon: HeartHandshake, accent: "text-sky-700", bg: "bg-sky-100",
    title: "Get help",
    body: "Shopping collected, bins taken out, a lift to the clinic — post a request and nearby neighbours will offer. You choose who helps, with safety tips at every step.",
    cta: { to: "/help-request/new", label: "Ask a neighbour", testId: "way-get-cta", intent: "need_help" },
  },
  {
    icon: MapPinned, accent: "text-forest", bg: "bg-forest/10",
    title: "Community",
    body: "Neighbourhood collectives run their own volunteering, gatherings and noticeboards. Join yours, start one of your own, or just show up to an event.",
    cta: { to: "/collectives", label: "Find your collective", testId: "way-community-cta" },
  },
];

const HOW_IT_WORKS_STEPS = [
  { icon: HandHeart, t: "1. Pick your causes", d: "Tell us what you care about and the skills you bring.", span: "md:col-span-4" },
  { icon: CalendarCheck, t: "2. Show up", d: "Apply to opportunities, attend events, log your hours with a story.", span: "md:col-span-4" },
  { icon: Award, t: "3. Earn recognition", d: "Organisations approve your hours. Unlock tiered badges and shareable certificates.", span: "md:col-span-4" },
];

const RECOGNITION_CARDS = [
  { icon: Medal, t: "Tiered badges", to: "/recognition?tab=badges", d: "Bronze to platinum as your approved hours grow — plus streaks for showing up again and again." },
  { icon: Trophy, t: "Community awards", to: "/recognition?tab=awards", d: "Seasonal and cause-specific awards voted by the community, worn on your public profile." },
  { icon: ScrollText, t: "Shareable certificates", to: "/recognition?tab=certificates", d: "Auto-generated, verifiable certificates for CVs, UCAS forms and LinkedIn." },
  { icon: Compass, t: "Leaderboards", to: "/recognition?tab=leaderboard", d: "Friendly local rankings — see your street, your city and your causes climb together." },
];

export function ThreeWaysSection() {
  return (
      <section className="max-w-7xl mx-auto px-6 pt-4 pb-10" data-testid="three-ways-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {THREE_WAYS.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.1}>
              <div data-testid={`way-card-${w.title.toLowerCase().replace(" ", "-")}`} className="bg-white border border-forest/10 rounded-3xl p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <div className={`w-12 h-12 rounded-2xl ${w.bg} flex items-center justify-center`}>
                  <w.icon className={`w-6 h-6 ${w.accent}`} />
                </div>
                <div className="font-display text-2xl text-forest mt-5">{w.title}</div>
                <p className="text-forest/70 mt-3 leading-relaxed text-[15px] flex-1">{w.body}</p>
                <div className="mt-6">
                  <Link to={w.cta.to} onClick={w.cta.intent ? () => rememberIntent(w.cta.intent) : undefined}>
                    <Button data-testid={w.cta.testId} className="rounded-full bg-forest hover:bg-forest-hover px-6 h-10 text-sm">
                      {w.cta.label} <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
  );
}

export function ValuesSection() {
  return (
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">What you value</div>
            <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Causes that need you</h2>
            <p className="text-forest/70 mt-5 text-lg leading-relaxed">Pick the causes that matter to you. We'll surface opportunities that fit your values, your skills and your schedule.</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-8 flex flex-wrap gap-3 lg:pt-14">
            {VALUES.map(v => (
              <Badge key={v.id} variant="outline" className="rounded-full border-forest/20 text-forest bg-white px-6 py-2.5 text-sm hover:bg-forest hover:text-white transition-colors duration-300 cursor-default">
                {v.label}
              </Badge>
            ))}
          </Reveal>
        </div>
      </section>
  );
}

export function HowItWorksSection() {
  return (
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {HOW_IT_WORKS_STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.12} className={s.span}>
              <div className="bg-white border border-forest/10 rounded-3xl p-10 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <div className="w-12 h-12 rounded-2xl bg-terracotta/10 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-terracotta" />
                </div>
                <div className="font-display text-2xl text-forest mt-6">{s.t}</div>
                <div className="text-forest/70 mt-3 leading-relaxed">{s.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
        {/* Phase 17-B item 4: quiet Pathways entry point under the cards */}
        <Reveal delay={0.3}>
          <div className="mt-6">
            <Link to="/pathways" data-testid="landing-pathways-link" className="text-sm font-medium text-forest/70 hover:text-terracotta transition-colors inline-flex items-center gap-1.5">
              Not sure where you fit? See the ways you can help <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Reveal>
      </section>
  );
}

export function NeighbourHelpSection() {
  return (
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="neighbour-help-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-sky-700 font-bold inline-flex items-center gap-2">
              <HeartHandshake className="w-3.5 h-3.5" /> Neighbour to neighbour
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Some help doesn't need a charity. Just a neighbour.</h2>
            <p className="text-forest/70 mt-5 text-lg leading-relaxed max-w-xl">
              Post what you need — shopping collected while you're unwell, the bins out, the dog
              walked — and volunteers nearby are alerted. They offer, <span className="text-sky-700 font-medium">you choose who helps</span>,
              and safety tips guide both sides before anyone commits. Your address stays private until you share it.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/help-request/new">
                <Button data-testid="neighbour-ask-cta" className="rounded-full bg-sky-600 hover:bg-sky-700 px-7 h-12 transition-transform hover:-translate-y-0.5">
                  Ask a neighbour <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/safety">
                <Button data-testid="neighbour-safety-cta" variant="outline" className="rounded-full border-forest/20 text-forest bg-white hover:bg-forest/5 hover:text-forest px-7 h-12">
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> Our safety guide
                </Button>
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <img src={NEIGHBOUR_IMAGE} alt="A volunteer helping an elderly neighbour with her shopping" className="w-full h-[420px] object-cover rounded-3xl border border-forest/10" loading="lazy" />
          </Reveal>
        </div>
      </section>
  );
}

export function CollectivesSection() {
  return (
      <section className="max-w-7xl mx-auto px-6 py-10" data-testid="collectives-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <img src={GATHERING_IMAGE} alt="Neighbours gathering on a residential street" className="w-full h-[420px] object-cover rounded-3xl border border-forest/10" loading="lazy" />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 order-1 lg:order-2">
            <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold inline-flex items-center gap-2">
              <MapPinned className="w-3.5 h-3.5" /> Collectives
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Your street, organised.</h2>
            <p className="text-forest/70 mt-5 text-lg leading-relaxed max-w-xl">
              Collectives are neighbourhood groups with their own noticeboard, volunteering and
              social gatherings — the litter pick on Saturday, the park picnic after. Join one
              near you, or start one and watch your street show up.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/collectives">
                <Button data-testid="collectives-cta" className="rounded-full bg-forest hover:bg-forest-hover px-7 h-12 transition-transform hover:-translate-y-0.5">
                  Find your collective <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/opportunities?type=community" data-testid="collectives-events-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:text-terracotta transition-colors self-center">
                Browse community events <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
  );
}

export function RecognitionSection() {
  return (
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="recognition-section">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold inline-flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> Recognition
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight max-w-2xl">Every hour counts. So we count every hour.</h2>
          <p className="text-forest/70 mt-4 max-w-xl text-lg">Hours are approved by the people you helped — then they turn into things you can keep and share.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {RECOGNITION_CARDS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              {/* Phase 17-B item 2: cards deep-link into the matching /recognition tab */}
              <Link to={s.to} data-testid={`recognition-card-${i}`} className="block civic-glass rounded-3xl p-7 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <s.icon className="w-7 h-7 text-terracotta" />
                <div className="font-display text-xl text-forest mt-4">{s.t}</div>
                <p className="text-sm text-forest/70 mt-2 leading-relaxed">{s.d}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
  );
}
